// src/views/Admin/Companies/components/InlineEditField/InlineEditField.tsx

import React, { useState, useRef, useEffect } from 'react';
import { HiOutlinePencilSquare, HiOutlineCheck, HiOutlineXMark } from 'react-icons/hi2';
import './InlineEditField.scss';

interface InlineEditFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel';
  className?: string;
  icon?: React.ReactNode;
  emptyText?: string;
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  placeholder = 'Ingresa un valor',
  type = 'text',
  className = '',
  icon,
  emptyText = 'No especificado'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(value);
    setError(null);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditValue(value);
    setError(null);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (editValue.trim() === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(editValue.trim());
      setIsEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !isSaving) {
      handleSave(e as any);
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value);
      setError(null);
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-field editing ${className}`} onClick={e => e.stopPropagation()}>
        <div className="edit-container">
          {icon && <span className="field-icon">{icon}</span>}
          <input
            ref={inputRef}
            type={type}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onClick={handleInputClick}
            placeholder={placeholder}
            disabled={isSaving}
            className={error ? 'has-error' : ''}
          />
          <div className="edit-actions">
            <button 
              className="save-btn" 
              onClick={handleSave}
              disabled={isSaving}
              title="Guardar"
            >
              {isSaving ? (
                <span className="spinner" />
              ) : (
                <HiOutlineCheck size={16} />
              )}
            </button>
            <button 
              className="cancel-btn" 
              onClick={handleCancel}
              disabled={isSaving}
              title="Cancelar"
            >
              <HiOutlineXMark size={16} />
            </button>
          </div>
        </div>
        {error && <span className="error-text">{error}</span>}
      </div>
    );
  }

  return (
    <div className={`inline-edit-field ${className}`} onClick={e => e.stopPropagation()}>
      <div className="display-container" onClick={handleStartEdit}>
        {icon && <span className="field-icon">{icon}</span>}
        <span className={`field-value ${!value ? 'empty' : ''}`}>
          {value || emptyText}
        </span>
        <button className="edit-trigger" title="Editar">
          <HiOutlinePencilSquare size={14} />
        </button>
      </div>
    </div>
  );
};

export default InlineEditField;
